import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeft, GripVertical, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { trpc } from "~/api/trpc";
import Content from "../components/layout/Content";
import StoreScheduleManager from "../components/store/new-schedule-manager/StoreScheduleManager";
import WaitingRoomsTab from "../components/store/WaitingRoomsTab";
import { FederalState } from "~/types/server-types/FederalState";

// ============================================================================
// Types
// ============================================================================

const NO_FEDERAL_STATE = "none";

const FEDERAL_STATE_OPTIONS: { value: FederalState; label: string }[] = [
    { value: FederalState.BW, label: "Baden-Württemberg" },
    { value: FederalState.BY, label: "Bayern" },
    { value: FederalState.BE, label: "Berlin" },
    { value: FederalState.BB, label: "Brandenburg" },
    { value: FederalState.HB, label: "Bremen" },
    { value: FederalState.HH, label: "Hamburg" },
    { value: FederalState.HE, label: "Hessen" },
    { value: FederalState.MV, label: "Mecklenburg-Vorpommern" },
    { value: FederalState.NI, label: "Niedersachsen" },
    { value: FederalState.NW, label: "Nordrhein-Westfalen" },
    { value: FederalState.RP, label: "Rheinland-Pfalz" },
    { value: FederalState.SL, label: "Saarland" },
    { value: FederalState.SN, label: "Sachsen" },
    { value: FederalState.ST, label: "Sachsen-Anhalt" },
    { value: FederalState.SH, label: "Schleswig-Holstein" },
    { value: FederalState.TH, label: "Thüringen" },
];

type GeneralFormFields = {
    name: string;
    address: string;
    city: string;
    zipCode: string;
    description: string;
    slug: string;
    externalId: string;
    contactEmail: string;
    contactPhone: string;
    locationLatitude: string;
    locationLongitude: string;
    federalState: FederalState | typeof NO_FEDERAL_STATE;
};

type BillingFormFields = {
    fullCompanyNameLegal: string;
    invoiceLegalRepresentative: string;
    invoiceWebsite: string;
    invoiceBankName: string;
    invoiceIban: string;
    invoiceBic: string;
    invoiceBankAccountHolder: string;
    invoiceTaxNumber: string;
    invoiceVatId: string;
    invoiceTaxOffice: string;
};

// ============================================================================
// General Tab
// ============================================================================

type GeneralTabProps = {
    storeId: number;
    store: {
        name: string;
        address: string;
        city: string;
        zipCode: string;
        description: string;
        slug: string | null;
        externalId: string | null;
        contactEmail: string;
        contactPhone: string;
        locationLatitude: number;
        locationLongitude: number;
        federalState: FederalState | null;
    };
};

const GeneralTab = ({ storeId, store }: GeneralTabProps) => {
    const utils = trpc.useUtils();

    const { register, handleSubmit, reset, control } = useForm<GeneralFormFields>({
        defaultValues: {
            name: store.name,
            address: store.address,
            city: store.city,
            zipCode: store.zipCode,
            description: store.description || "",
            slug: store.slug || "",
            externalId: store.externalId || "",
            contactEmail: store.contactEmail || "",
            contactPhone: store.contactPhone || "",
            locationLatitude: String(store.locationLatitude),
            locationLongitude: String(store.locationLongitude),
            federalState: store.federalState ?? NO_FEDERAL_STATE,
        },
    });

    useEffect(() => {
        reset({
            name: store.name,
            address: store.address,
            city: store.city,
            zipCode: store.zipCode,
            description: store.description || "",
            slug: store.slug || "",
            externalId: store.externalId || "",
            contactEmail: store.contactEmail || "",
            contactPhone: store.contactPhone || "",
            locationLatitude: String(store.locationLatitude),
            locationLongitude: String(store.locationLongitude),
            federalState: store.federalState ?? NO_FEDERAL_STATE,
        });
    }, [store, reset]);

    const updateMutation = trpc.stores.update.useMutation({
        onSuccess: () => {
            utils.stores.getAll.invalidate();
            utils.stores.getOne.invalidate({ id: storeId });
            toast.success("Allgemeine Daten gespeichert.");
        },
        onError: (error) => {
            toast.error("Fehler beim Speichern", { description: error.message });
        },
    });

    const onSubmit = (values: GeneralFormFields) => {
        updateMutation.mutate({
            id: storeId,
            name: values.name,
            address: values.address,
            city: values.city,
            zipCode: values.zipCode,
            description: values.description,
            slug: values.slug || null,
            externalId: values.externalId,
            contactEmail: values.contactEmail,
            contactPhone: values.contactPhone,
            locationLatitude: values.locationLatitude ? parseFloat(values.locationLatitude) : 0,
            locationLongitude: values.locationLongitude ? parseFloat(values.locationLongitude) : 0,
            federalState: values.federalState !== NO_FEDERAL_STATE ? values.federalState : null,
        });
    };

    const isSaving = updateMutation.isPending;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Allgemeine Daten</CardTitle>
                    <CardDescription>Grundlegende Informationen zur Filiale</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="name">Name *</Label>
                            <Input id="name" {...register("name")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="address">Adresse *</Label>
                            <Input id="address" {...register("address")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="city">Stadt *</Label>
                            <Input id="city" {...register("city")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="zipCode">Postleitzahl *</Label>
                            <Input id="zipCode" {...register("zipCode")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Beschreibung</Label>
                            <Input id="description" {...register("description")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="slug">Slug</Label>
                            <Input id="slug" {...register("slug")} placeholder="z.B. mein-store" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="externalId">Externe ID</Label>
                            <Input id="externalId" {...register("externalId")} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contactEmail">Kontakt E-Mail</Label>
                            <Input id="contactEmail" {...register("contactEmail")} type="email" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contactPhone">Kontakt Telefon</Label>
                            <Input id="contactPhone" {...register("contactPhone")} type="tel" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="locationLatitude">Breitengrad (Latitude)</Label>
                            <Input id="locationLatitude" {...register("locationLatitude")} type="number" step="any" placeholder="z.B. 52.5200" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="locationLongitude">Längengrad (Longitude)</Label>
                            <Input id="locationLongitude" {...register("locationLongitude")} type="number" step="any" placeholder="z.B. 13.4050" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="federalState">Bundesland (für Feiertage)</Label>
                            <Controller
                                name="federalState"
                                control={control}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger id="federalState">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NO_FEDERAL_STATE}>Keins (nur bundesweite Feiertage)</SelectItem>
                                            {FEDERAL_STATE_OPTIONS.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
            </Button>
        </form>
    );
};

// ============================================================================
// Billing / Invoice Tab
// ============================================================================

type BillingTabProps = {
    storeId: number;
    store: {
        fullCompanyNameLegal: string | null;
        invoiceLegalRepresentative: string | null;
        invoiceWebsite: string | null;
        invoiceBankName: string | null;
        invoiceIban: string | null;
        invoiceBic: string | null;
        invoiceBankAccountHolder: string | null;
        invoiceTaxNumber: string | null;
        invoiceVatId: string | null;
        invoiceTaxOffice: string | null;
    };
};

const BillingTab = ({ storeId, store }: BillingTabProps) => {
    const utils = trpc.useUtils();

    const { register, handleSubmit, reset } = useForm<BillingFormFields>({
        defaultValues: {
            fullCompanyNameLegal: store.fullCompanyNameLegal || "",
            invoiceLegalRepresentative: store.invoiceLegalRepresentative || "",
            invoiceWebsite: store.invoiceWebsite || "",
            invoiceBankName: store.invoiceBankName || "",
            invoiceIban: store.invoiceIban || "",
            invoiceBic: store.invoiceBic || "",
            invoiceBankAccountHolder: store.invoiceBankAccountHolder || "",
            invoiceTaxNumber: store.invoiceTaxNumber || "",
            invoiceVatId: store.invoiceVatId || "",
            invoiceTaxOffice: store.invoiceTaxOffice || "",
        },
    });

    useEffect(() => {
        reset({
            fullCompanyNameLegal: store.fullCompanyNameLegal || "",
            invoiceLegalRepresentative: store.invoiceLegalRepresentative || "",
            invoiceWebsite: store.invoiceWebsite || "",
            invoiceBankName: store.invoiceBankName || "",
            invoiceIban: store.invoiceIban || "",
            invoiceBic: store.invoiceBic || "",
            invoiceBankAccountHolder: store.invoiceBankAccountHolder || "",
            invoiceTaxNumber: store.invoiceTaxNumber || "",
            invoiceVatId: store.invoiceVatId || "",
            invoiceTaxOffice: store.invoiceTaxOffice || "",
        });
    }, [store, reset]);

    const updateMutation = trpc.stores.update.useMutation({
        onSuccess: () => {
            utils.stores.getAll.invalidate();
            utils.stores.getOne.invalidate({ id: storeId });
            toast.success("Rechnungsdaten gespeichert.");
        },
        onError: (error) => {
            toast.error("Fehler beim Speichern", { description: error.message });
        },
    });

    const onSubmit = (values: BillingFormFields) => {
        updateMutation.mutate({
            id: storeId,
            fullCompanyNameLegal: values.fullCompanyNameLegal || null,
            invoiceLegalRepresentative: values.invoiceLegalRepresentative || null,
            invoiceWebsite: values.invoiceWebsite || null,
            invoiceBankName: values.invoiceBankName || null,
            invoiceIban: values.invoiceIban || null,
            invoiceBic: values.invoiceBic || null,
            invoiceBankAccountHolder: values.invoiceBankAccountHolder || null,
            invoiceTaxNumber: values.invoiceTaxNumber || null,
            invoiceVatId: values.invoiceVatId || null,
            invoiceTaxOffice: values.invoiceTaxOffice || null,
        });
    };

    const isSaving = updateMutation.isPending;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Company / Legal */}
            <Card>
                <CardHeader>
                    <CardTitle>Unternehmensdaten</CardTitle>
                    <CardDescription>Rechtliche Informationen, die auf Rechnungen erscheinen</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="fullCompanyNameLegal">Vollständiger Firmenname</Label>
                            <Input id="fullCompanyNameLegal" {...register("fullCompanyNameLegal")} placeholder="z.B. kalialab GmbH" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceLegalRepresentative">Gesetzlicher Vertreter</Label>
                            <Input id="invoiceLegalRepresentative" {...register("invoiceLegalRepresentative")} placeholder="z.B. Dr. Max Mustermann" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceWebsite">Website</Label>
                            <Input id="invoiceWebsite" {...register("invoiceWebsite")} placeholder="z.B. www.kalialab.de" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Bank Details */}
            <Card>
                <CardHeader>
                    <CardTitle>Bankverbindung</CardTitle>
                    <CardDescription>Bankdaten im Rechnungsfooter</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="invoiceBankName">Bank</Label>
                            <Input id="invoiceBankName" {...register("invoiceBankName")} placeholder="z.B. BERLINER VOLKSBANK" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceIban">IBAN</Label>
                            <Input id="invoiceIban" {...register("invoiceIban")} placeholder="z.B. DE90 1009 0000 2990 2370 10" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceBic">BIC</Label>
                            <Input id="invoiceBic" {...register("invoiceBic")} placeholder="z.B. BEVODEBBXXX" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceBankAccountHolder">Kontoinhaber</Label>
                            <Input id="invoiceBankAccountHolder" {...register("invoiceBankAccountHolder")} placeholder="z.B. kalialab GmbH" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tax Details */}
            <Card>
                <CardHeader>
                    <CardTitle>Steuerdaten</CardTitle>
                    <CardDescription>Steuerinformationen im Rechnungsfooter</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="invoiceTaxNumber">Steuernummer</Label>
                            <Input id="invoiceTaxNumber" {...register("invoiceTaxNumber")} placeholder="z.B. 42/736/02629" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceVatId">USt-IdNr.</Label>
                            <Input id="invoiceVatId" {...register("invoiceVatId")} placeholder="z.B. DE345081026" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoiceTaxOffice">Finanzamt</Label>
                            <Input id="invoiceTaxOffice" {...register("invoiceTaxOffice")} placeholder="z.B. Finanzamt Hamburg" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
            </Button>
        </form>
    );
};

// ============================================================================
// Photos Tab
// ============================================================================

type StoreImage = {
    id: string;
    publicSrc: string;
    sequence: number;
};

type PhotosTabProps = {
    storeId: number;
    store: {
        clinicGoogleMapsImgSrc: string;
        images: StoreImage[];
    };
};

const SortableImage = ({ image, onDelete, isDeleting }: { image: StoreImage; onDelete: (id: string) => void; isDeleting: boolean }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="relative group h-40 w-56 shrink-0 overflow-hidden rounded-lg border bg-muted">
            <img src={image.publicSrc} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            <button
                type="button"
                {...attributes}
                {...listeners}
                className="absolute top-2 left-2 rounded bg-white/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <button
                type="button"
                onClick={() => onDelete(image.id)}
                disabled={isDeleting}
                className="absolute top-2 right-2 rounded bg-white/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
            >
                <Trash2 className="h-4 w-4 text-red-600" />
            </button>
        </div>
    );
};

const PhotosTab = ({ storeId, store }: PhotosTabProps) => {
    const utils = trpc.useUtils();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const googleMapsFileInputRef = useRef<HTMLInputElement>(null);
    const [orderedImages, setOrderedImages] = useState<StoreImage[]>(store.images);

    useEffect(() => {
        setOrderedImages(store.images);
    }, [store.images]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const uploadPhotoMutation = trpc.stores.uploadPhoto.useMutation({
        onSuccess: () => {
            utils.stores.getOne.invalidate({ id: storeId });
            toast.success("Foto hochgeladen.");
        },
        onError: (error) => {
            toast.error("Fehler beim Hochladen", { description: error.message });
        },
    });

    const deleteImageMutation = trpc.stores.deleteStoreImage.useMutation({
        onSuccess: () => {
            utils.stores.getOne.invalidate({ id: storeId });
            toast.success("Foto gelöscht.");
        },
        onError: (error) => {
            toast.error("Fehler beim Löschen", { description: error.message });
        },
    });

    const reorderMutation = trpc.stores.reorderStoreImages.useMutation({
        onError: (error) => {
            toast.error("Fehler beim Sortieren", { description: error.message });
        },
    });

    const uploadGoogleMapsMutation = trpc.stores.uploadGoogleMapsPhoto.useMutation({
        onSuccess: () => {
            utils.stores.getOne.invalidate({ id: storeId });
            toast.success("Google Maps Bild hochgeladen.");
        },
        onError: (error) => {
            toast.error("Fehler beim Hochladen", { description: error.message });
        },
    });

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            uploadPhotoMutation.mutate({ storeId, imageBase64: reader.result as string });
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const handleGoogleMapsSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            uploadGoogleMapsMutation.mutate({ storeId, imageBase64: reader.result as string });
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedImages.findIndex((img) => img.id === active.id);
        const newIndex = orderedImages.findIndex((img) => img.id === over.id);
        const reordered = arrayMove(orderedImages, oldIndex, newIndex);
        setOrderedImages(reordered);

        reorderMutation.mutate({
            imageOrders: reordered.map((img, idx) => ({ id: img.id, sequence: idx + 1 })),
        });
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Filialfotos</CardTitle>
                    <CardDescription>Fotos der Filiale, die Kunden angezeigt werden. Reihenfolge per Drag & Drop ändern.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {orderedImages.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={orderedImages.map((img) => img.id)} strategy={horizontalListSortingStrategy}>
                                <div className="flex flex-wrap gap-4">
                                    {orderedImages.map((image) => (
                                        <SortableImage
                                            key={image.id}
                                            image={image}
                                            onDelete={(id) => deleteImageMutation.mutate({ imageId: id })}
                                            isDeleting={deleteImageMutation.isPending}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <p className="text-sm text-muted-foreground">Noch keine Fotos hochgeladen.</p>
                    )}
                    <div>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
                        <Button
                            type="button"
                            variant="outline"
                            disabled={uploadPhotoMutation.isPending}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploadPhotoMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ImagePlus className="mr-2 h-4 w-4" />
                            )}
                            Foto hinzufügen
                        </Button>
                        <p className="mt-1 text-xs text-muted-foreground">JPG, PNG oder WebP. Wird auf max. 800px skaliert.</p>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Google Maps Bild</CardTitle>
                    <CardDescription>Bild der Filiale auf Google Maps</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-start gap-6">
                        <div className="relative h-40 w-64 shrink-0 overflow-hidden rounded-lg border bg-muted">
                            {store.clinicGoogleMapsImgSrc ? (
                                <img src={store.clinicGoogleMapsImgSrc} alt="Google Maps" className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                    <ImagePlus className="h-10 w-10" />
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <input ref={googleMapsFileInputRef} type="file" accept="image/*" onChange={handleGoogleMapsSelect} className="hidden" />
                            <Button
                                type="button"
                                variant="outline"
                                disabled={uploadGoogleMapsMutation.isPending}
                                onClick={() => googleMapsFileInputRef.current?.click()}
                            >
                                {uploadGoogleMapsMutation.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <ImagePlus className="mr-2 h-4 w-4" />
                                )}
                                {store.clinicGoogleMapsImgSrc ? "Bild ändern" : "Bild hochladen"}
                            </Button>
                            <p className="text-xs text-muted-foreground">JPG, PNG oder WebP. Wird auf max. 800px skaliert.</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// ============================================================================
// Main Store Page
// ============================================================================

const Store = () => {
    const params = useParams();
    const navigate = useNavigate();

    const id = parseInt(params.id!, 10);

    const { data: store, isLoading, isFetching } = trpc.stores.getOne.useQuery(
        { id },
        { enabled: !!id },
    );

    if (isLoading) {
        return (
            <Content>
                <div className="space-y-6">
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-64" />
                    </div>
                    <Skeleton className="h-10 w-96" />
                    <Skeleton className="h-[400px] w-full" />
                </div>
            </Content>
        );
    }

    if (!store) {
        return (
            <Content>
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <h2 className="text-xl font-semibold text-gray-900">Filiale nicht gefunden</h2>
                    <p className="text-gray-500">Die angeforderte Filiale konnte nicht gefunden werden.</p>
                    <Button variant="outline" onClick={() => navigate("/stores")}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Zurück zur Übersicht
                    </Button>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/stores")}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <h1 className="text-2xl font-semibold">{store.name}</h1>
                    {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </div>

                {/* Tabs */}
                <Tabs defaultValue="general">
                    <TabsList>
                        <TabsTrigger value="general">Allgemein</TabsTrigger>
                        <TabsTrigger value="photos">Fotos</TabsTrigger>
                        <TabsTrigger value="billing">Rechnungsdaten</TabsTrigger>
                        <TabsTrigger value="schedule">Öffnungszeiten</TabsTrigger>
                        <TabsTrigger value="waitingRooms">Warteräume</TabsTrigger>
                    </TabsList>

                    <TabsContent value="general" className="mt-4">
                        <GeneralTab storeId={id} store={store} />
                    </TabsContent>

                    <TabsContent value="photos" className="mt-4">
                        <PhotosTab storeId={id} store={store} />
                    </TabsContent>

                    <TabsContent value="billing" className="mt-4">
                        <BillingTab storeId={id} store={store} />
                    </TabsContent>

                    <TabsContent value="schedule" className="mt-4">
                        <StoreScheduleManager storeId={id} />
                    </TabsContent>

                    <TabsContent value="waitingRooms" className="mt-4">
                        <WaitingRoomsTab storeId={id} />
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default Store;
